/**
 * Results View - Display Component
 *
 * Final step of the Energy Certificate Simulator wizard.
 * Displays EP calculation results, energy class, and recommendations.
 */

import {
  Stack,
  Paper,
  Text,
  Title,
  Group,
  Badge,
  Progress,
  SimpleGrid,
  ThemeIcon,
  Accordion,
  Alert,
  Divider,
  Card,
} from '@mantine/core';
import {
  IconFlame,
  IconDroplet,
  IconSnowflake,
  IconBolt,
  IconAlertTriangle,
  IconCircleCheck,
  IconTrendingDown,
  IconCurrencyZloty,
  IconClock,
  IconLeaf,
} from '@tabler/icons-react';
import type { SimulatorResult, EnergyClass, Recommendation } from '../../types';
import {
  ENERGY_CLASS_COLORS,
  ENERGY_CLASS_DESCRIPTIONS,
  ENERGY_CLASS_LABELS,
  POLISH_AVERAGE_EP,
} from '../../constants/energyClassThresholds';
import { HeatingCostSection } from '../../heating-cost/components/HeatingCostSection';
import { HeatingCostErrorBoundary } from '../../heating-cost/components/HeatingCostErrorBoundary';
import { FEATURE_FLAGS } from '../../heating-cost/constants/featureFlags';

// =============================================================================
// CONSTANTS
// =============================================================================

const PRIORITY_COLORS: Record<string, string> = {
  high: 'red',
  medium: 'yellow',
  low: 'green',
};

const PRIORITY_LABELS: Record<string, string> = {
  high: 'Wysoki',
  medium: 'Średni',
  low: 'Niski',
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface EnergyClassBadgeProps {
  energyClass: EnergyClass;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

function EnergyClassBadge({ energyClass, size = 'lg' }: EnergyClassBadgeProps) {
  const color = ENERGY_CLASS_COLORS[energyClass];
  const sizeMap = {
    sm: { fontSize: 14, padding: '4px 8px' },
    md: { fontSize: 18, padding: '6px 12px' },
    lg: { fontSize: 24, padding: '8px 16px' },
    xl: { fontSize: 36, padding: '12px 24px' },
  };

  return (
    <Badge
      size="xl"
      radius="md"
      style={{
        backgroundColor: color,
        color: energyClass === 'C' ? 'var(--mantine-color-black)' : 'var(--mantine-color-white)',
        fontSize: sizeMap[size].fontSize,
        padding: sizeMap[size].padding,
        fontWeight: 700,
      }}
    >
      {energyClass}
    </Badge>
  );
}

interface EnergyScaleProps {
  energyClass: EnergyClass;
}

function EnergyScale({ energyClass }: EnergyScaleProps) {
  const classes: EnergyClass[] = ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];

  return (
    <Stack gap="xs">
      <Group justify="space-between" gap="xs">
        {classes.map((cls) => (
          <Paper
            key={cls}
            p="xs"
            radius="sm"
            style={{
              backgroundColor: ENERGY_CLASS_COLORS[cls],
              color: cls === 'C' ? 'var(--mantine-color-black)' : 'var(--mantine-color-white)',
              flex: 1,
              textAlign: 'center',
              fontWeight: cls === energyClass ? 700 : 400,
              border: cls === energyClass ? '3px solid var(--mantine-color-dark-9)' : 'none',
              opacity: cls === energyClass ? 1 : 0.7,
            }}
          >
            <Text size="sm" fw={cls === energyClass ? 700 : 400}>
              {cls}
            </Text>
          </Paper>
        ))}
      </Group>
      <Text size="xs" c="dimmed" ta="center">
        Klasa energetyczna A+ (najlepsza) → G (najgorsza)
      </Text>
    </Stack>
  );
}

interface EPComponentCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  percentage?: number;
}

function EPComponentCard({ title, value, icon, color, percentage }: EPComponentCardProps) {
  return (
    <Paper p="md" withBorder radius="md">
      <Group gap="sm">
        <ThemeIcon variant="light" color={color} size="lg">
          {icon}
        </ThemeIcon>
        <div style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">
            {title}
          </Text>
          <Text fw={600} size="lg">
            {value.toFixed(1)} kWh/m²/rok
          </Text>
          {percentage !== undefined && (
            <Progress value={percentage} color={color} size="sm" mt="xs" />
          )}
        </div>
      </Group>
    </Paper>
  );
}

interface RecommendationCardProps {
  recommendation: Recommendation;
}

function RecommendationCard({ recommendation }: RecommendationCardProps) {
  return (
    <Accordion.Item value={recommendation.id}>
      <Accordion.Control>
        <Group justify="space-between" wrap="nowrap" pr="md">
          <div>
            <Text fw={500}>{recommendation.title}</Text>
            <Text size="xs" c="dimmed">
              Redukcja EP: {recommendation.epReduction.toFixed(0)} kWh/m²/rok
            </Text>
          </div>
          <Badge color={PRIORITY_COLORS[recommendation.priority]}>
            {PRIORITY_LABELS[recommendation.priority]}
          </Badge>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="sm">
          <Text size="sm">{recommendation.description}</Text>

          <SimpleGrid cols={3}>
            <Paper p="sm" bg="gray.0" radius="sm">
              <Group gap="xs">
                <IconCurrencyZloty size={16} />
                <div>
                  <Text size="xs" c="dimmed">
                    Szacunkowy koszt
                  </Text>
                  <Text size="sm" fw={500}>
                    {recommendation.estimatedCost.min.toLocaleString('pl-PL')} -{' '}
                    {recommendation.estimatedCost.max.toLocaleString('pl-PL')} zł
                  </Text>
                </div>
              </Group>
            </Paper>

            <Paper p="sm" bg="gray.0" radius="sm">
              <Group gap="xs">
                <IconTrendingDown size={16} />
                <div>
                  <Text size="xs" c="dimmed">
                    Roczne oszczędności
                  </Text>
                  <Text size="sm" fw={500}>
                    {recommendation.estimatedSavings.toLocaleString('pl-PL')} kWh
                  </Text>
                </div>
              </Group>
            </Paper>

            <Paper p="sm" bg="gray.0" radius="sm">
              <Group gap="xs">
                <IconClock size={16} />
                <div>
                  <Text size="xs" c="dimmed">
                    Zwrot inwestycji
                  </Text>
                  <Text size="sm" fw={500}>
                    {recommendation.paybackYears.min}-{recommendation.paybackYears.max} lat
                  </Text>
                </div>
              </Group>
            </Paper>
          </SimpleGrid>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export interface ResultsViewProps {
  /** Calculation results */
  result: SimulatorResult;
  /** Building area for context */
  buildingArea?: number;
  /** Callback to restart simulation */
  onRestart?: () => void;
  /** Current heating system (for heating cost comparison) */
  heatingSystem?: string;
  /** Age of current heating system */
  systemAge?: string;
}

/**
 * Results display component
 *
 * Shows:
 * - Energy class with visual scale
 * - EP indicator breakdown
 * - Comparison with WT 2021 limit
 * - Energy consumption values
 * - Modernization recommendations
 */
export function ResultsView({ result, buildingArea, heatingSystem, systemAge }: ResultsViewProps) {
  const {
    epTotal,
    epHeating,
    epHotWater,
    epCooling,
    energyClass,
    limitEP,
    exceedsLimit,
    usefulEnergy,
    finalEnergy,
    primaryEnergy,
    recommendations,
  } = result;

  // Calculate percentages for EP components
  const heatingPercentage = (epHeating / epTotal) * 100;
  const hotWaterPercentage = (epHotWater / epTotal) * 100;
  const coolingPercentage = (epCooling / epTotal) * 100;

  // Comparison with Polish average
  const vsPolishAverage = ((epTotal - POLISH_AVERAGE_EP) / POLISH_AVERAGE_EP) * 100;
  const isBetterThanAverage = epTotal < POLISH_AVERAGE_EP;

  return (
    <Stack gap="xl">
      {/* Main result card */}
      <Paper p="xl" withBorder radius="lg" shadow="sm">
        <Stack gap="lg" align="center">
          <Title order={3} ta="center">
            Wynik symulacji certyfikatu energetycznego
          </Title>

          {/* Energy class display */}
          <Stack gap="md" align="center">
            <EnergyClassBadge energyClass={energyClass} size="xl" />
            <Text size="lg" c="dimmed" ta="center">
              {ENERGY_CLASS_LABELS[energyClass]}
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={400}>
              {ENERGY_CLASS_DESCRIPTIONS[energyClass]}
            </Text>
          </Stack>

          {/* EP value */}
          <Card withBorder radius="md" p="lg" w="100%" maw={300}>
            <Stack gap="xs" align="center">
              <Text size="xs" c="dimmed" tt="uppercase">
                Wskaźnik EP
              </Text>
              <Text
                size="xl"
                fw={700}
                style={{
                  fontSize: 48,
                  color: ENERGY_CLASS_COLORS[energyClass],
                }}
              >
                {epTotal.toFixed(0)}
              </Text>
              <Text size="sm" c="dimmed">
                kWh/m²/rok
              </Text>
            </Stack>
          </Card>

          {/* Energy scale */}
          <EnergyScale energyClass={energyClass} />
        </Stack>
      </Paper>

      {/* WT 2021 compliance */}
      {exceedsLimit ? (
        <Alert
          variant="light"
          color="red"
          title="Przekroczenie limitu WT 2021"
          icon={<IconAlertTriangle size={20} />}
        >
          <Text size="sm">
            Wskaźnik EP ({epTotal.toFixed(0)} kWh/m²/rok) przekracza limit dla nowych budynków
            wynoszący {limitEP} kWh/m²/rok. Budynek wymaga modernizacji, aby spełnić
            aktualne wymagania techniczne.
          </Text>
        </Alert>
      ) : (
        <Alert
          variant="light"
          color="green"
          title="Zgodność z WT 2021"
          icon={<IconCircleCheck size={20} />}
        >
          <Text size="sm">
            Wskaźnik EP ({epTotal.toFixed(0)} kWh/m²/rok) mieści się w limicie dla nowych
            budynków wynoszącym {limitEP} kWh/m²/rok.
          </Text>
        </Alert>
      )}

      {/* Comparison with Polish average */}
      <Paper p="md" withBorder radius="md">
        <Group justify="space-between" align="center">
          <div>
            <Text fw={500}>Porównanie ze średnią krajową</Text>
            <Text size="xs" c="dimmed">
              Średni EP dla budynków mieszkalnych w Polsce: {POLISH_AVERAGE_EP} kWh/m²/rok
            </Text>
          </div>
          <Badge
            size="lg"
            color={isBetterThanAverage ? 'green' : 'red'}
            variant="light"
          >
            {isBetterThanAverage ? '' : '+'}
            {vsPolishAverage.toFixed(0)}%
          </Badge>
        </Group>
      </Paper>

      {/* EP breakdown */}
      <Stack gap="sm">
        <Title order={4}>Składniki wskaźnika EP</Title>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <EPComponentCard
            title="Ogrzewanie (EP_H)"
            value={epHeating}
            icon={<IconFlame size={20} />}
            color="red"
            percentage={heatingPercentage}
          />
          <EPComponentCard
            title="Ciepła woda (EP_W)"
            value={epHotWater}
            icon={<IconDroplet size={20} />}
            color="blue"
            percentage={hotWaterPercentage}
          />
          <EPComponentCard
            title="Chłodzenie (EP_C)"
            value={epCooling}
            icon={<IconSnowflake size={20} />}
            color="cyan"
            percentage={coolingPercentage}
          />
        </SimpleGrid>
      </Stack>

      {/* Energy values */}
      <Stack gap="sm">
        <Title order={4}>Zapotrzebowanie na energię</Title>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Paper p="md" withBorder>
            <Stack gap="xs" align="center">
              <ThemeIcon variant="light" color="gray" size="lg">
                <IconBolt size={20} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">
                Energia użytkowa (EU)
              </Text>
              <Text fw={600} size="lg">
                {usefulEnergy.toLocaleString('pl-PL')} kWh/rok
              </Text>
              <Text size="xs" c="dimmed">
                Potrzebna do ogrzania budynku
              </Text>
            </Stack>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="xs" align="center">
              <ThemeIcon variant="light" color="orange" size="lg">
                <IconFlame size={20} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">
                Energia końcowa (EK)
              </Text>
              <Text fw={600} size="lg">
                {finalEnergy.toLocaleString('pl-PL')} kWh/rok
              </Text>
              <Text size="xs" c="dimmed">
                Zużyta przez systemy grzewcze
              </Text>
            </Stack>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="xs" align="center">
              <ThemeIcon variant="light" color="green" size="lg">
                <IconLeaf size={20} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">
                Energia pierwotna (EP)
              </Text>
              <Text fw={600} size="lg">
                {primaryEnergy.toLocaleString('pl-PL')} kWh/rok
              </Text>
              <Text size="xs" c="dimmed">
                Całkowity ślad energetyczny
              </Text>
            </Stack>
          </Paper>
        </SimpleGrid>
      </Stack>

      <Divider />

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Stack gap="sm">
          <Title order={4}>
            Zalecenia modernizacyjne ({recommendations.length})
          </Title>
          <Text size="sm" c="dimmed">
            Poniżej przedstawiono rekomendacje poprawy efektywności energetycznej
            budynku, posortowane według potencjału redukcji EP.
          </Text>

          <Accordion variant="contained" radius="md">
            {recommendations.map((rec) => (
              <RecommendationCard key={rec.id} recommendation={rec} />
            ))}
          </Accordion>
        </Stack>
      )}

      {/* Heating Cost Comparison */}
      {FEATURE_FLAGS.HEATING_COST_COMPARISON && result.usefulEnergy > 0 && (
        <HeatingCostErrorBoundary>
          <Divider />
          <HeatingCostSection
            usefulHeatingEnergy={result.usefulEnergy}
            hotWaterEnergy={result.hotWaterUsefulEnergy ?? 0}
            currentSystem={heatingSystem}
            systemAge={systemAge}
            buildingArea={buildingArea}
          />
        </HeatingCostErrorBoundary>
      )}

      {/* Disclaimer */}
      <Paper p="md" bg="gray.0" radius="md">
        <Text size="xs" c="dimmed">
          <strong>Zastrzeżenie:</strong> Niniejsza symulacja ma charakter orientacyjny
          i nie zastępuje oficjalnego świadectwa charakterystyki energetycznej
          sporządzonego przez uprawnionego audytora. Wyniki opierają się na uproszczonym
          modelu obliczeniowym i mogą różnić się od rzeczywistych wartości. Dokładność
          szacowana na ~80%.
        </Text>
      </Paper>
    </Stack>
  );
}

export default ResultsView;
